import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Badge, Icon } from '@openedx/paragon';
import { useIntl } from '@edx/frontend-platform/i18n';
import { useOpenAiChat } from '@blend-ed/blendxplatform';
import {
  RiSearch2Line,
  RiSparkling2Line,
  RiCalendarTodoLine,
  RiTeamLine,
  RiCompassLine,
} from '@remixicon/react';

import { useMyTeams } from '../../../gql/hooks/useMyTeams';
import { useCalendarEvents } from '../../../gql/hooks/useCalendarEvents';
import messages from './messages';
import './GetStartedCards.scss';

const GetStartedCard = ({
  icon,
  title,
  body,
  to,
  onClick,
  badge,
  ariaDisabled,
  modifier,
}) => {
  const modifierClass = modifier ? ` gsc-card--${modifier}` : '';
  const inner = (
    <>
      <div className="gsc-card__icon-badge">
        <Icon src={icon} className="gsc-card__icon" />
      </div>
      <div className="gsc-card__text">
        <div className="gsc-card__title">{title}</div>
        <div className="gsc-card__body">{body}</div>
        {badge && (
          <div className="gsc-card__badge">
            <Badge variant="light">{badge}</Badge>
          </div>
        )}
      </div>
    </>
  );

  if (to) {
    return (
      <Link to={to} className={`gsc-card gsc-card--link${modifierClass}`}>
        {inner}
      </Link>
    );
  }

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`gsc-card gsc-card--button${modifierClass}`}
      >
        {inner}
      </button>
    );
  }

  return (
    <div
      className={`gsc-card gsc-card--static${modifierClass}`}
      aria-disabled={ariaDisabled || undefined}
    >
      {inner}
    </div>
  );
};

GetStartedCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  to: PropTypes.string,
  onClick: PropTypes.func,
  badge: PropTypes.string,
  ariaDisabled: PropTypes.bool,
  modifier: PropTypes.string,
};

GetStartedCard.defaultProps = {
  to: null,
  onClick: null,
  badge: null,
  ariaDisabled: false,
  modifier: null,
};

const GetStartedCards = ({ variant }) => {
  const intl = useIntl();
  const openAiChat = useOpenAiChat();
  const { teams } = useMyTeams();
  const { events } = useCalendarEvents(true);

  const resolvedVariant = (() => {
    if (variant) {
      return variant;
    }
    if (Array.isArray(teams) && teams.length > 0) {
      return 'team';
    }
    if (Array.isArray(events) && events.length > 0) {
      return 'schedule';
    }
    return 'topic';
  })();

  const thirdCard = (() => {
    if (resolvedVariant === 'team') {
      return (
        <GetStartedCard
          icon={RiTeamLine}
          title={intl.formatMessage(messages.teamActivityTitle)}
          body={intl.formatMessage(messages.teamActivityBody)}
          ariaDisabled
        />
      );
    }
    if (resolvedVariant === 'schedule') {
      return (
        <GetStartedCard
          icon={RiCalendarTodoLine}
          title={intl.formatMessage(messages.viewScheduleTitle)}
          body={intl.formatMessage(messages.viewScheduleBody)}
          to="/live-sessions"
        />
      );
    }
    return (
      <GetStartedCard
        icon={RiCompassLine}
        title={intl.formatMessage(messages.exploreTopicTitle)}
        body={intl.formatMessage(messages.exploreTopicBody)}
        to="/discover"
      />
    );
  })();

  return (
    <div className="get-started-cards">
      <GetStartedCard
        icon={RiSearch2Line}
        title={intl.formatMessage(messages.exploreCoursesTitle)}
        body={intl.formatMessage(messages.exploreCoursesBody)}
        to="/discover"
      />
      <GetStartedCard
        icon={RiSparkling2Line}
        title={intl.formatMessage(messages.askAiTutorTitle)}
        body={intl.formatMessage(messages.askAiTutorBody)}
        onClick={openAiChat}
        modifier="ai"
      />
      {thirdCard}
    </div>
  );
};

GetStartedCards.propTypes = {
  variant: PropTypes.oneOf(['team', 'schedule', 'topic']),
};

GetStartedCards.defaultProps = {
  variant: null,
};

export default GetStartedCards;
